import React from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";

import { UseFormReturn } from "react-hook-form";
import { withSnackbar, WithSnackbarProps } from "notistack";
import { t } from "@lingui/macro";

import { DefaultTypedResponse, ErrorResponseApi, GetCollectionListResponse } from "models/response";
import { EditContentState } from "./models";
import { Content, MusicCueSheet } from "models";

import { Box } from "@mui/material";

import NavigationBack from "commons/navigation/navigation-back/navigation-back";
import { ZPlayLoader, ZPlayNetworkService, ZPlayUploadLoader } from "commons";

import { ContentProducerService } from "services/content-producer-service";
import { CreateAndEditContentDetailForm } from "components/backoffice/content";
import { ContentType } from "commons/form-constants";
import { UpdateContentResponse } from "models/response/update-content-response";
import { S3MultipartUploadData, VODUploadType } from "models/request/upload-content-response";
import { MultipartConfirmUploadRequest, VODPartConfirmData } from "models/request/multipart-confirm-upload-request";
import { PromiseResult } from "aws-sdk/lib/request";
import { AWSError, S3 } from "aws-sdk";
import { AbortMultipartUploadRequest, UploadPartOutput, UploadPartRequest } from "aws-sdk/clients/s3";

const MULTIPART_PART_SIZE_IN_MB = 80;
class EditContent extends React.Component<UseFormReturn & WithSnackbarProps & RouteComponentProps<any>, EditContentState>{

    private s3;
    private multipartUploadedPartsETagList?: VODPartConfirmData[];
    private multipartNotUploadedPartList: { partNumber: number, start: number, end: number }[];
    private totalParts: number;
    private partsUploaded: number;

    constructor(props: any) {

        super(props);

        this.s3 = new S3({
            credentials: {
                accessKeyId: process.env.REACT_APP_AWS_ROLE_AKID ?? "",
                secretAccessKey: process.env.REACT_APP_AWS_ROLE_SECRET ?? ""
            },
            region: "eu-central-1"
        });

        this.multipartUploadedPartsETagList = [];
        this.multipartNotUploadedPartList = [];
        this.totalParts = 0;
        this.partsUploaded = 0;

        this.state = {
            isLoading: true,
            content: null,
            collectionsList: null,
            getContentError: false,
            musicCueSheetList: null,
            vodFile: null,
            thumbnailFile: null,
            isThumbnailUpdated: false,
            isVodUpdated: false,
            isUploading: false,
            uploadStageMessage: "",
            currentUploadPercentage: 0,
            uploadType: null,
            vodUploadUrl: null,
            subtitlesForUpload: []
        };
    }

    public componentDidMount() {
        this.getContentById();
    };

    getContentSuccessCallback = (data: Content) => {
        this.setState(() => ({
            content: data
        }))
        ContentProducerService.getCollectionList(this.getCollectionListSuccessCallback, this.getCollectionListErrorCallback);
    }

    getContentErrorCallback = (err: ErrorResponseApi) => {
        this.setState(() => ({
            isLoading: false,
            content: null,
            getContentError: true
        }));
    }

    getCollectionListSuccessCallback = (data: GetCollectionListResponse) => {
        this.setState(() => ({
            collectionsList: data
        }))

        ContentProducerService.getMusicCueSheetsListByContentId(this.state.content!.id, this.getMusicCueSheetsListSuccessCallback, this.getMusicCueSheetListErrorCallback);
    }

    getCollectionListErrorCallback = (err: ErrorResponseApi) => {

        let emptyCollectionList: GetCollectionListResponse = {
            awardList: { list: [], size: 0 },
            countryList: { list: [], size: 0 },
            festivalList: { list: [], size: 0 },
            genreList: { list: [], size: 0 },
            languageList: { list: [], size: 0 },
            tagList: { list: [], size: 0 },
            contentIssuesList: { list:[], size: 0 },
            seriesList: { list:[], size: 0 }
        };

        this.setState(() => ({
            isLoading: false,
            collectionsList: emptyCollectionList,
            musicCueSheetList: { size: 0, list: [] }
        }));

        this.props.enqueueSnackbar(t({
            id: "ec-error-get-collection-list-error",
            message: "Si è verificato un errore. Non è stato possibile recuperare i dati"
        }), {
            variant: "error",
        });
    }

    getMusicCueSheetsListSuccessCallback = (data: DefaultTypedResponse<MusicCueSheet>) => {
        this.setState(() => ({
            musicCueSheetList: data,
            isLoading: false
        }))
    }

    getMusicCueSheetListErrorCallback = (err: ErrorResponseApi) => {

        this.setState(() => ({
            isLoading: false,
            musicCueSheetList: { size: 0, list: [] },
        }));

        this.props.enqueueSnackbar(t({
            id: "ec-error-get-music-cue-sheets-list-error",
            message: "Si è verificato un errore. Non è stato possibile recuperare i dati"
        }), {
            variant: "error",
        });
    }

    private onSubmit = (request: any) => {

        if (!this.checkDetailsStepValidity(request))
            return

        let thumbnailFile = request.thumbnailFile;

        if(request.isThumbnailUpdated){
            request.thumbnailData = {
                fileName: thumbnailFile.name,
                format: thumbnailFile.type.split("/")[1]
            };
        } else {
            request.thumbnailData = this.state.content?.thumbnailData
        }

        let vodFile: any = null;

        if(request.isVodUpdated && request.type === ContentType.VOD){

            vodFile = request.vodFile;

            request.vodMimeType = vodFile.type;
            request.fileSize = vodFile.size;

            request.vodAdditionalInfos = {
                fileName: vodFile.name,
                format: vodFile.type.split("/")[1]
            };
        }

        let subtitles = request.subtitles;
        let subtitlesForUploads : any = []
        request.subtitles = []


        if (subtitles) {
            for (let subtitle of subtitles) {
                if(subtitle.subtitleFileS3Key) {
                    request.subtitles?.push(subtitle);
                }else{
                    request.subtitles?.push({
                        fileName: subtitle.fileName,
                        language: subtitle.language,
                        format: subtitle.file.type.split("/")[1]
                    });
                    subtitlesForUploads.push(subtitle);
                }
            }
        }

        this.setState(() => ({
            subtitlesForUpload: subtitlesForUploads,
            vodFile: vodFile,
            thumbnailFile: thumbnailFile,
            isVodUpdated: request.isVodUpdated,
            isThumbnailUpdated: request.isThumbnailUpdated,
            isUploading: true,
            uploadStageMessage: t({
                id: "ec-update-pre-stage",
                message: "Preparazione alla modifica"
            })
        }))

        ContentProducerService.updateContent(this.state.content?.id!, request, this.startUpload, this.onSubmitCallbackError)
    }

    onSubmitCallback = () => {

        this.props.enqueueSnackbar(t({
            id: "ec-error-submit-update-content",
            message: "Il contenuto è stato aggiornato"
        }), {
            variant: "success",
        });

        this.getContentById();
    }

    onSubmitCallbackError = (err: ErrorResponseApi) => {

        this.setState(() => ({
            isLoading: false,
            isUploading: false
        }));

        this.props.enqueueSnackbar(t({
            id: "ec-error-submit-update-content-error",
            message: "Si è verificato un errore. Non è stato possibile modificare il contenuto"
        }), {
            variant: "error",
        });
    }

    private startUpload = (data: UpdateContentResponse) => {

        this.multipartUploadedPartsETagList = [];
        this.totalParts = 0;
        this.partsUploaded = 0;

        this.setState(() => ({
            uploadedContent: data.content,
            uploadType: data.uploadType,
            subtitlesUploadUrl: data.s3PresignedSubtitlesFileList,
            vodUploadUrl: (data.uploadType === VODUploadType.DIRECT && data.s3PresignedUploadUrl) ? data.s3PresignedUploadUrl : null,
            vodMultipartUploadData: data.uploadType === VODUploadType.MULTIPART ? data.s3MultipartUploadData : null,
        }));

        if(this.state.subtitlesForUpload.length>0){
            this.startSubtitlesUpload()
        }

        if(this.state.isThumbnailUpdated) {
            
            this.setState(() => ({
                uploadStageMessage: t({
                    id: "ec-upload-thumbnail-stage",
                    message: "Caricamento della thumbnail in corso..."
                })
            }));

            ZPlayNetworkService.xhrMultipartUpload(data.s3PresignedThumbnailUrl ?? "", this.state.thumbnailFile, this.progressCallback)
            .then(this.confirmThumbUpload)
            .catch(this.errorCallback)
        }

        if(!this.state.isThumbnailUpdated && this.state.isVodUpdated){
            this.startVODNetworkUploadProcess();
        }

        if(!this.state.isThumbnailUpdated && !this.state.isVodUpdated){
            this.onSubmitCallback();
        }
    };

    private errorCallback = (err: any) => {
        this.props.enqueueSnackbar(t({
            id: "ec-error-submit-update-content-error",
            message: "Si è verificato un errore. Non è stato possibile modificare il contenuto"
        }), {
            variant: "error",
        });
        this.setState(() => ({ isUploading: false }));
    };

    private confirmThumbUpload = () => {

        this.setState(() => ({
            currentUploadPercentage: 0
        }));

        if (this.state.uploadedContent)
            ContentProducerService.confirmThumbnailUpload(this.state.uploadedContent.id, this.thumbUploadUploadEnd,
                this.errorCallback);
    };

    private getContentById = () => {

        this.setState(() => ({
            isUploading: false,
            isLoading: true
        }));

        ContentProducerService.getContentById(this.props.match.params.id, this.getContentSuccessCallback, this.getContentErrorCallback);
    }

    private thumbUploadUploadEnd = () => {

        if (!this.state.isVodUpdated) {
            this.getContentById();
        }

        if (this.state.isVodUpdated) {
            this.startVODNetworkUploadProcess();
        }

        /* if (this.state.uploadedContent?.type === ContentType.LIVE) {
            this.getContentById();
        } */
    };

    private startVODNetworkUploadProcess = () => {

        this.setState(() => ({
            currentUploadPercentage: 0,
            uploadStageMessage: t({
                id: "ec-upload-vod-stage",
                message: "Caricamento del video in corso..."
            })
        }));

        if (this.state.uploadType === VODUploadType.DIRECT && this.state.vodUploadUrl) {

            ZPlayNetworkService.xhrMultipartUpload(this.state.vodUploadUrl, this.state.vodFile, this.progressCallback)
                .then(this.confirmFileUpload)
                .catch(this.errorCallback)
        }

        if (this.state.uploadType === VODUploadType.MULTIPART && this.state.vodFile && this.state.vodMultipartUploadData) {
            this.uploadParts(this.state.vodMultipartUploadData);
        }
    }

    private startSubtitlesUpload = () => {
        this.subtitlesUploadRecursiveInternal(this.state.subtitlesForUpload[0], 0)
    }

    private subtitlesUploadRecursiveInternal = (subtitleToUpload: any, index: any) => {
        if (subtitleToUpload && index < this.state.subtitlesForUpload.length) {

            this.setState(() => ({
                uploadStageMessage: `${t({
                    id: "ec-upload-subtitles-stage",
                    message: "Caricamento dei sottotitoli per la lingua"
                })}:${subtitleToUpload.language}`
            }));
            ZPlayNetworkService.xhrMultipartUpload(this.checkWichUrlUseToUploadSubtitle(subtitleToUpload.language), subtitleToUpload.file, this.progressCallback)
                .then(() => { let newIndex = ++index; this.subtitlesUploadRecursiveInternal(this.state.subtitlesForUpload[newIndex], newIndex) })
                .catch(this.errorCallback)
        }
    }

    private checkWichUrlUseToUploadSubtitle = (subtitleLanguage: string) => {

        let correctSubtitleUrl: string = ""

        if (this.state.subtitlesUploadUrl)
            for (let subtitleUrl of this.state.subtitlesUploadUrl) {
                if (subtitleUrl.includes(subtitleLanguage))
                    correctSubtitleUrl = subtitleUrl;
            }

        return correctSubtitleUrl;
    }

    private uploadParts = (data: S3MultipartUploadData) => {

        let file = this.state.vodFile

        if (!file) {

            this.props.enqueueSnackbar(t({
                id: "ec-error-upload-multipart",
                message: "Si è verificato un errore durante il caricamento"
            }), {
                variant: "error",
            });

        } else {

            const fileSize = file.size;
            const partSize = (1024 * 1024) * MULTIPART_PART_SIZE_IN_MB
            this.totalParts = Math.ceil(fileSize / partSize)

            let uploadPromises: Promise<PromiseResult<S3.UploadPartOutput, AWSError>>[] = []

            let partStart = 0;

            for (let i = 0; i < this.totalParts; i++) {

                let partStartForCallback = partStart
                let partEnd = partStart + partSize

                let part = (partEnd < file.size) ? file.slice(partStart, partEnd) : file.slice(partStart, file.size);

                let currentPartNumber: number = i + 1;

                let s3UploadPartData: UploadPartRequest = {
                    Bucket: data.bucket,
                    Key: data.fileKey,
                    PartNumber: currentPartNumber,
                    UploadId: data.uploadId,
                    Body: part
                }

                let uploadPromise = this.s3.uploadPart(s3UploadPartData).promise();

                uploadPromise.then((data: PromiseResult<UploadPartOutput, AWSError>) => {

                    this.partsUploaded++;

                    this.setState(() => ({
                        currentUploadPercentage: Math.round((this.partsUploaded / this.totalParts) * 100)
                    }))

                    this.multipartUploadedPartsETagList?.push({
                        PartNumber: currentPartNumber,
                        ETag: data["ETag"]
                    })
                })

                uploadPromise.catch(() => {
                    this.multipartNotUploadedPartList.push(
                        {
                            partNumber: currentPartNumber,
                            start: partStartForCallback,
                            end: partEnd
                        }
                    );
                });

                uploadPromises.push(uploadPromise);

                partStart = partEnd;
            }

            Promise.allSettled(uploadPromises)
                .then((results) => {
                    if (this.multipartNotUploadedPartList.length !== 0)
                        this.retryNotUploadedParts(0, data);
                    else {

                        /* INTEGRARE EDIT SOTTOTITOLI */
                        /* if (this.state.currentUploadRequest?.subtitles && this.state.currentUploadRequest.subtitles.length > 0) {
                            this.startSubtitlesUpload({});
                        } else {
                            this.confirmFileUpload({});
                        } */

                        this.confirmFileUpload({});
                    }
                })
        }

    }

    private retryNotUploadedParts = (retryCount: number, data: S3MultipartUploadData) => {

        let uploadPromises = [];

        if (retryCount >= 4) {
            this.props.enqueueSnackbar(t({
                id: "ec-error-upload-multipart-retry",
                message: "Si è verificato un errore durante il caricamento"
            }), {
                variant: "error",
            });
            return
        }


        let file = this.state.vodFile

        if (!file) {
            this.props.enqueueSnackbar(t({
                id: "ec-error-upload-multipart-nofile",
                message: "Si è verificato un errore durante il caricamento"
            }), {
                variant: "error",
            });
        } else {

            let i = 0;
            for (const element of this.multipartNotUploadedPartList) {

                let partStart = element.start
                let partEnd = element.end

                let part = (partEnd < file.size) ? file.slice(partStart, partEnd) : file.slice(partStart, file.size);

                let currentPartNumber: number = element.partNumber;

                let s3UploadPartData: UploadPartRequest = {
                    Bucket: data.bucket,
                    Key: data.fileKey,
                    PartNumber: currentPartNumber,
                    UploadId: data.uploadId,
                    Body: part
                }

                let uploadPromise = this.s3.uploadPart(s3UploadPartData).promise();

                uploadPromise.then((data: PromiseResult<UploadPartOutput, AWSError>) => {

                    this.partsUploaded++;

                    this.setState(() => ({
                        currentUploadPercentage: Math.round((this.partsUploaded / this.totalParts) * 100)
                    }))

                    this.multipartUploadedPartsETagList?.push({
                        PartNumber: currentPartNumber,
                        ETag: data["ETag"]
                    })

                    this.multipartNotUploadedPartList.splice(i, 1);
                })

                uploadPromises.push(uploadPromise)
            }

            Promise.allSettled(uploadPromises)
                .then(() => {
                    if (this.multipartNotUploadedPartList.length !== 0)
                        this.retryNotUploadedParts(++retryCount, data);
                    else {

                        /* INTEGRARE EDIT SOTTOTITOLI */
                        /* if (this.state.currentUploadRequest?.subtitles && this.state.currentUploadRequest.subtitles.length > 0) {
                            this.startSubtitlesUpload({});
                        } else {
                            this.confirmFileUpload({});
                        } */

                        this.confirmFileUpload({});
                    }
                })
                .catch(this.onMultipartUploadError)
        }


    }

    private onMultipartUploadError = () => {

        if (this.state.vodMultipartUploadData) {

            let abortMultipartRequest: AbortMultipartUploadRequest = {
                Bucket: this.state.vodMultipartUploadData.bucket,
                Key: this.state.vodMultipartUploadData.fileKey,
                UploadId: this.state.vodMultipartUploadData.uploadId
            }

            this.s3.abortMultipartUpload(abortMultipartRequest).promise()
                .then(
                    () => console.log("Successfully aborted Multipart upload"),
                    err => { console.log(`Error ${JSON.stringify(err)}`) }
                );

        }

        this.setState(() => ({
            isUploading: false
        }));

        this.props.enqueueSnackbar(t({
            id: "ec-error-update",
            message: "Si è verificato un errore durante l'aggiornamento del contenuto"
        }), {
            variant: "error",
        });
    }

    private confirmFileUpload = (data: any) => {

        if (this.state.uploadedContent) {

            let requestBody: MultipartConfirmUploadRequest | {}

            if (this.state.uploadType === VODUploadType.MULTIPART) {
                requestBody = {
                    partList: this.multipartUploadedPartsETagList
                }
            } else {
                requestBody = {}
            }

            ContentProducerService.confirmVODFileUpload(this.state.uploadedContent.id, requestBody, this.getContentById,
                this.errorCallback);
        }
    };

    private progressCallback = (percentage: number) => {
        this.setState(() => ({ currentUploadPercentage: percentage }))
    }

    private checkDetailsStepValidity = (submitData: any): boolean => {

        let errorsArray: string[] = [];
        let warningArray: string[] = [];

        if (!submitData.segregationType || submitData.segregationType === "")
            errorsArray.push(t({
                id: "ec-form-validation-no-segregation",
                message: "È necessario inserire la segregazione"
            }));

        if (submitData.title.length === 0)
            errorsArray.push(t({
                id: "ec-form-validation-no-title",
                message: "È necessario inserire almeno un titolo"
            }));

        if (submitData.synopsis.length === 0)
            errorsArray.push(t({
                id: "ec-form-validation-no-synopsis",
                message: "È necessario inserire almeno una sinossi"
            }));
            
        if(submitData.isThumbnailUpdated && !submitData.thumbnailFile.name)
            errorsArray.push(t({
                id: "ec-form-validation-no-thumbnail-file",
                message: "È necessario inserire un'immagine di copertina"
            }));

        if(!submitData.isThumbnailUpdated && !submitData.thumbnailFile.fileName)
            errorsArray.push(t({
                id: "ec-form-validation-no-thumbnail-file",
                message: "È necessario inserire un'immagine di copertina"
            }));

        if (!submitData.language)
            errorsArray.push(t({
                id: "ec-form-validation-no-language",
                message: "Non è stata caricata nessuna lingua del film"
            }));

        if (!Number(submitData.productionYear) && submitData.productionYear!=="0")
            errorsArray.push(t({
                id: "ec-form-validation-no-production-year",
                message: "L'anno di produzione non è valido"
            }));

        if (submitData.director.length === 0)
            warningArray.push(t({
                id: "ec-form-validation-no-director",
                message: "Non è stato indicato nessun regista"
            }));

        if (!Number(submitData.durationInSeconds))
            errorsArray.push(t({
                id: "ec-form-validation-no-duration",
                message: "Durata del film non indicata o indicata in maniera errata"
            }));

        if (submitData.seriesData && !submitData.seriesData.seriesId)
        errorsArray.push(t({
            id: "ec-form-validation-serie-err",
            message: "Serie non indicata"
        }));

        if (submitData.seriesData && !Number(submitData.seriesData.episodeNumber))
        errorsArray.push(t({
            id: "ec-form-validation-episode-err",
            message: "Episodio non indicato o indicato in maniera errata"
        }));

        if (submitData.seriesData && !Number(submitData.seriesData.season))
        errorsArray.push(t({
            id: "ec-form-validation-season-err",
            message: "Stagione non indicata o indicata in maniera errata"
        }));

        if (warningArray.length > 0)
            this.props.enqueueSnackbar(warningArray.join(". "),
                { variant: "warning" });

        if (errorsArray.length > 0)
            this.props.enqueueSnackbar(errorsArray.join(". "),
                { variant: "error" });

        return errorsArray.length === 0;
    }


    render() {
        return <Box sx={{ p: 5 }}>
            {this.state.isLoading && <ZPlayLoader />}
            {this.state.getContentError && <Redirect to={"/not-found"} />}
            {this.state.isUploading &&
                    <ZPlayUploadLoader progress={this.state.currentUploadPercentage}
                        message={this.state.uploadStageMessage} />}
            {!this.state.isLoading && <>
                <NavigationBack/>
                {this.state.content !== null && 
                    <Box maxWidth={"1000px"} padding={"30px 10px 10px"} margin={"0 auto"}>
                        <CreateAndEditContentDetailForm
                            onDetailSubmit={this.onSubmit}
                            content={this.state.content}
                            collectionsList={this.state.collectionsList}
                            musicCueSheetsList={this.state.musicCueSheetList!.list}
                        />
                    </Box>}
            </>}
        </Box>;
    }
}

export default withSnackbar(EditContent);